// Extracted so other consumers (e.g. the onboarding dashboard) can build the
// same auth headers without duplicating the key-vs-direct-code branching.
export function buildAuthHeaders(config) {
  if (config?.bearerToken) {
    return { Authorization: `Bearer ${config.bearerToken}` };
  }
  if (config?.directAccessCode) {
    return { 'X-qsync-Direct-Access-Code': config.directAccessCode };
  }
  if (config?.keyId && config?.secretKey) {
    return { 'X-qsync-Access-Key-Id': config.keyId, 'X-qsync-Secret-Access-Key': config.secretKey };
  }
  return null;
}

// Shared request path for both reads and writes. Extracts the backend's
// `{ message }` JSON error body when present so callers can surface it
// directly instead of a raw "HTTP 400: {...}" string.
async function request(url, config, { method = 'GET', body, signal } = {}) {
  const headers = { ...(buildAuthHeaders(config) || {}) };
  // FormData must go out untouched: the browser sets Content-Type with the
  // multipart boundary, and setting it by hand makes the boundary missing and
  // the upload unparseable server-side.
  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
  if (body !== undefined && !isFormData) headers['Content-Type'] = 'application/json';

  const res = await fetch(url, {
    method,
    headers,
    body: body === undefined ? undefined : isFormData ? body : JSON.stringify(body),
    signal,
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    let message = '';
    try {
      message = JSON.parse(text)?.message || text;
    } catch {
      // Non-JSON responses (including Express HTML 404 pages) use the safe
      // HTTP message below rather than being rendered into the UI.
      message = /^\s*</.test(text) ? '' : text;
    }
    const error = new Error(message || `Request failed (HTTP ${res.status}). The backend endpoint may not be deployed yet.`);
    error.status = res.status;
    throw error;
  }

  if (res.status === 204) return null;
  return res.json();
}

const fetchWithAuth = (url, config) => request(url, config);
const assertNoDroppedActivationStatus = payload => {
  if (payload?.activationStatus === 'DROPPED') {
    throw new Error('Clients cannot be marked as DROPPED.');
  }
};

export const fetchClients       = (base, cfg)       => fetchWithAuth(`${base}/api/campaign/clients`, cfg);
export const fetchUsers         = (base, cfg)       => fetchWithAuth(`${base}/api/campaign/users`, cfg);
export const fetchWeeklyMetrics = (base, cfg, date) => fetchWithAuth(`${base}/api/campaign/metrics/weekly?date=${date}`, cfg);

// ─── Campaigns (live) ───────────────────────────────────────────────────────
// Live ticket-derived campaign metrics, sourced from the scoreboard service so
// the numbers reconcile with the action-page KPI cards. Unlike the three
// helpers above (internal access code), this one takes the caller's own
// internal-user JWT — the backend scopes the response to their clients.
export const fetchCampaignLiveMetrics = (base, cfg, { refresh = false, signal } = {}) =>
  request(`${base}/api/campaign/live-metrics${refresh ? '?refresh=true' : ''}`, cfg, { signal });

// ─── Internal Users module (RAG-141) ────────────────────────────────────────
// All of these expect `cfg` to carry the caller's own internal-user JWT as
// `{ bearerToken }` (see AuthContext) — the backend scopes every response to
// that actor's role (ADMIN sees everything; KAM/AE see only their own clients).

export const fetchInternalClients = (base, cfg) =>
  request(`${base}/api/internalUsers/clients`, cfg).then(r => r.data);

// Full editable field set for one client (PAN, sponsor email, GST number,
// monthly invoice amount, KAM/AE emails) — the list above omits these to
// keep that query lightweight.
export const fetchInternalClientDetail = (base, cfg, clientId) =>
  request(`${base}/api/internalUsers/clients/${clientId}`, cfg).then(r => r.data);

export const createInternalClient = (base, cfg, payload) => {
  assertNoDroppedActivationStatus(payload);
  return request(`${base}/api/internalUsers/clients`, cfg, { method: 'POST', body: payload });
};

export const updateInternalClient = (base, cfg, clientId, payload) => {
  assertNoDroppedActivationStatus(payload);
  return request(`${base}/api/internalUsers/clients/${clientId}`, cfg, { method: 'PATCH', body: payload });
};

export const upsertClientBilling = (base, cfg, clientId, payload) =>
  request(`${base}/api/internalUsers/clients/${clientId}/billing`, cfg, { method: 'PUT', body: payload });

export const fetchInternalUsersList = (base, cfg) =>
  request(`${base}/api/internalUsers`, cfg).then(r => r.data);

// Name/email/role of ACTIVE staff — readable by every internal role (unlike
// the full list above, which is admin-only). Feeds the KAM/AE dropdowns.
export const fetchInternalUserDirectory = (base, cfg) =>
  request(`${base}/api/internalUsers/directory`, cfg).then(r => r.data);

export const createInternalUserApi = (base, cfg, payload) =>
  request(`${base}/api/internalUsers`, cfg, { method: 'POST', body: payload });

export const resetInternalUserPasswordApi = (base, cfg, internalUserId, newPassword) =>
  request(`${base}/api/internalUsers/${internalUserId}/reset-password`, cfg, {
    method: 'POST',
    body: { newPassword },
  });

export const updateInternalUserRoleApi = (base, cfg, internalUserId, role) =>
  request(`${base}/api/internalUsers/${internalUserId}/role`, cfg, {
    method: 'PATCH',
    body: { role },
  }).catch(error => {
    if (error.status === 404) {
      throw new Error('Role updates are not available on the current backend yet. Deploy the matching o2c-backend feature branch and try again.');
    }
    throw error;
  });

// ─── Dispute Console (Nakad Ops) ─────────────────────────────────────────────
// Backed by o2c-backend's /api/actions/DisputeOps* endpoints — KAM/ADMIN only
// (DISPUTE_OPS_ACTION policy). Cross-client by design: this is the one module
// that isn't scoped to a single brand, so no activeClient is involved here.

export const fetchDisputeOpsQueue = (base, cfg, filters = {}) => {
  const params = new URLSearchParams();
  if (filters.brand) params.set('brand', filters.brand);
  if (filters.platform) params.set('platform', filters.platform);
  if (filters.status) params.set('status', filters.status);
  if (filters.search) params.set('search', filters.search);
  const qs = params.toString();
  return request(`${base}/api/actions/DisputeOpsQueue${qs ? `?${qs}` : ''}`, cfg);
};

// Shared with the brand-facing detail view (d2c-compass) — an internal token
// can read any dispute, no ownership check on this side.
export const fetchDisputeDetail = (base, cfg, disputeId) =>
  request(`${base}/api/actions/DisputeDetail?disputeId=${disputeId}`, cfg);

// `attachments` is only used by logPlatformReply (the documents the platform
// sent back). With files present the request switches to multipart, which
// cannot carry a nested object — so payload rides along as a JSON string and
// the backend's parseOpsActionPayload reads either shape.
export const runDisputeOpsAction = (base, cfg, disputeId, action, payload = {}, attachments = []) => {
  if (!attachments.length) {
    return request(`${base}/api/actions/DisputeOpsAction`, cfg, {
      method: 'POST',
      body: { disputeId, action, payload },
    });
  }
  const form = new FormData();
  form.append('disputeId', String(disputeId));
  form.append('action', action);
  form.append('payload', JSON.stringify(payload));
  for (const file of attachments) form.append('attachments', file);
  return request(`${base}/api/actions/DisputeOpsAction`, cfg, { method: 'POST', body: form });
};

// Signed S3 URL for a debit-note PDF linked to the invoice. Separate endpoint
// from fetchDocumentUrl below: DN docs live in their own bucket and are served
// by the dn router. docId is a dn_documents id, so it must go with
// "debit_note": "invoice" checks platform_invoice_doc first, whose ids overlap
// and resolve to another client's document. clientId (the dispute's client) is
// required for internal tokens and scopes the lookup to that client's docs.
export const fetchDnDocumentUrl = (base, cfg, docId, docSource = 'debit_note', clientId) => {
  const params = new URLSearchParams({ docSource });
  if (clientId != null) params.set('clientId', String(clientId));
  return request(`${base}/api/dn/RedirectToDoc/${docId}?${params}`, cfg);
};

// Signed S3 URL for one evidence document — same RedirectToDoc mechanism the
// brand-facing dashboard uses for invoice/DN docs.
export const fetchDocumentUrl = (base, cfg, docId) =>
  request(`${base}/api/actions/RedirectToDoc/${docId}`, cfg);

// DN document coverage (RAG-220). Read-only, no parameters — returns one row
// per active dn_pdf config with total / fetched / remaining.
export const fetchDnVisibility = (base, cfg) =>
  request(`${base}/api/dn-visibility/summary`, cfg);
